import { Link as RouterLink } from 'react-router-dom';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import {
  catalogApiRef,
  entityRouteRef,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';
import { groupResponses } from '@backstage/plugin-kubernetes-common';
import { useKubernetesObjects } from '@backstage/plugin-kubernetes-react';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import useAsync from 'react-use/esm/useAsync';

const NAMESPACE_ANNOTATION = 'backstage.io/kubernetes-namespace';
const REFRESH_MS = 600000;

// 짝 환경 = 같은 metadata.name + 다른 catalog namespace (예 svc-arcus-default ↔ svc-arcus-default-dev)
const SiblingEnvLink = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const entityRoute = useRouteRef(entityRouteRef);
  const ownNamespace = entity.metadata.namespace ?? DEFAULT_NAMESPACE;
  const system = entity.spec?.system as string | undefined;

  const { value } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Component', 'metadata.name': entity.metadata.name },
      fields: ['kind', 'metadata.name', 'metadata.namespace', 'spec.system'],
    });
    return items.find(
      e =>
        (e.metadata.namespace ?? DEFAULT_NAMESPACE) !== ownNamespace &&
        (!system || e.spec?.system === system),
    );
  }, [entity.metadata.name, ownNamespace, system]);

  if (!value) return null;
  const siblingNamespace = value.metadata.namespace ?? DEFAULT_NAMESPACE;
  return (
    <>
      {' → '}
      <Link
        component={RouterLink}
        to={entityRoute({
          kind: value.kind,
          namespace: siblingNamespace,
          name: value.metadata.name,
        })}
      >
        {siblingNamespace}/{value.metadata.name}
      </Link>
    </>
  );
};

/**
 * 엔티티 페이지 상단 배너(결함 47).
 * - 조회 성공 + 파드 0 → 「배포된 파드가 없습니다」 + 짝 환경 링크
 * - 조회 오류(전체 error 또는 클러스터별 errors) + 파드 0 → 「확인하지 못했습니다」(파드 0 단정 금지)
 * 파드 유무 = 포털이 이미 쓰는 Kubernetes 플러그인 응답(useKubernetesObjects) 재사용.
 */
export const EmptyEnvBanner = () => {
  const { entity } = useEntity();
  const namespace = entity.metadata.annotations?.[NAMESPACE_ANNOTATION];
  const { kubernetesObjects, loading, error } = useKubernetesObjects(entity, REFRESH_MS);

  if (!namespace || loading) return null;

  const items = kubernetesObjects?.items ?? [];
  const podCount = items.reduce((sum, item) => {
    const pods = groupResponses(item.resources).pods ?? [];
    return sum + pods.filter(p => p.status?.phase === 'Running' || p.status?.phase === 'Pending').length;
  }, 0);
  if (podCount > 0) return null;

  const queryFailed = Boolean(error) || items.some(item => (item.errors ?? []).length > 0);
  if (queryFailed) {
    return (
      <Alert severity="warning" data-testid="empty-env-banner-error" style={{ marginBottom: 16 }}>
        <Typography variant="body2" component="span">
          이 환경({namespace})의 배포 상태를 확인하지 못했습니다(조회 오류)
        </Typography>
      </Alert>
    );
  }
  if (!kubernetesObjects) return null;

  return (
    <Alert severity="info" data-testid="empty-env-banner" style={{ marginBottom: 16 }}>
      <Typography variant="body2" component="span">
        이 환경({namespace})에는 배포된 파드가 없습니다
        <SiblingEnvLink />
      </Typography>
    </Alert>
  );
};
